import { promptText } from '../../lib/prompt-dialog.js';
import { newSoundDoc, slugSoundName, SOUND_KINDS, type SoundDoc, type SoundKind } from '../../lib/sound-doc.js';
import { filesOfKind, useProjectFiles } from '../../state/project-files.js';
import { useSound } from '../../state/sound.js';
import { SaveIndicator } from '../scene/SceneTab.js';
import { IconButton, MenuButton, type MenuItem } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';

/**
 * Sound tab header: open/new, document name, kind switcher, presets,
 * variant, render status/controls, view switch, undo/redo and save state.
 */

export const KIND_LABEL: Record<SoundKind, string> = { sfx: 'Sound effect', instrument: 'Instrument', song: 'Song', voice: 'Voice' };

/** Ask for a name and create `sounds/<name>.sound.json` from a blank doc of `kind` (or `from`). */
export async function newSound(kind: SoundKind = 'sfx', from?: SoundDoc): Promise<void> {
  const name = (await promptText(`Name of the new ${KIND_LABEL[from?.kind ?? kind].toLowerCase()}`, from?.name ?? KIND_LABEL[kind]))?.trim();
  if (!name) return;
  const path = name.includes('/') ? `${name.replace(/\.sound\.json$/i, '')}.sound.json` : `sounds/${slugSoundName(name)}.sound.json`;
  const doc: SoundDoc = from
    ? { ...structuredClone(from), name, export: { ...(from.export ?? {}), path: `audio/${slugSoundName(name)}.wav` } }
    : newSoundDoc(name, kind);
  await useSound.getState().create(path, doc);
  useProjectFiles.getState().refreshSoon();
}

function SoundPicker() {
  const entries = useProjectFiles((s) => s.entries);
  const path = useSound((s) => s.path);
  const docs = filesOfKind(entries, 'sound');
  const items = (): MenuItem[] => [
    { id: 'h', label: 'New', heading: true },
    ...SOUND_KINDS.map((k) => ({ id: `new:${k}`, label: `New ${KIND_LABEL[k].toLowerCase()}…`, icon: 'plus' as const, run: () => void newSound(k) })),
    { id: 'h2', label: 'Sounds', heading: true },
    ...docs.map((p) => ({ id: p, label: p, icon: 'audio' as const, run: () => void useSound.getState().open(p) })),
    ...(docs.length === 0 ? [{ id: 'none', label: 'No *.sound.json files yet', disabled: true }] : []),
  ];
  return (
    <MenuButton className="picker" title="Open or create a sound" items={items}>
      <Icon name="audio" size={13} />
      <span className="picker-label">{path ? path.replace(/^sounds\//, '').replace(/\.sound\.json$/, '') : 'Open sound…'}</span>
      <Icon name="chevronDown" size={11} className="muted" />
    </MenuButton>
  );
}

function RenderStatus() {
  const render = useSound((s) => s.render);
  const autoRender = useSound((s) => s.autoRender);
  const s = useSound.getState();
  const busy = render.status === 'rendering';
  const label =
    render.status === 'rendering'
      ? `${render.stage || 'Rendering'}${render.progress !== null && render.progress < 1 ? ` ${Math.round(render.progress * 100)}%` : '…'}`
      : render.status === 'queued'
        ? 'Changed — rendering soon'
        : render.status === 'error'
          ? 'Render failed'
          : render.status === 'done'
            ? `Rendered${render.source === 'server' ? ' (backend)' : ''}${render.took !== null ? ` in ${render.took < 1000 ? `${render.took} ms` : `${(render.took / 1000).toFixed(1)} s`}` : ''}`
            : 'Not rendered';
  return (
    <div className="tb-group snd-render">
      <button type="button" className="tb-btn" onClick={() => void s.renderNow()} disabled={busy} title="Render now (Ctrl+Enter)">
        <Icon name="refresh" size={13} /> Render
      </button>
      <MenuButton
        className="tb-btn"
        title="Render options"
        items={[
          { id: 'auto', label: autoRender ? 'Auto-render after edits: on' : 'Auto-render after edits: off', icon: autoRender ? 'check' : undefined, run: () => s.setAutoRender(!autoRender) },
          { id: 'server', label: 'Render with the prism CLI (backend)', icon: 'terminal', run: () => void s.renderNow({ serverOnly: true }) },
        ]}
      >
        <Icon name="chevronDown" size={11} />
      </MenuButton>
      <span
        className={`snd-render-status is-${render.status}`}
        role="status"
        aria-live="polite"
        title={render.error ?? (render.warnings.length ? render.warnings.join('\n') : label)}
      >
        {busy && <span className="snd-spinner" aria-hidden="true" />}
        {label}
        {busy && render.progress !== null && (
          <span className="snd-render-progress" aria-hidden="true">
            <span style={{ width: `${Math.round(render.progress * 100)}%` }} />
          </span>
        )}
      </span>
    </div>
  );
}

export function SoundHeader() {
  const doc = useSound((s) => s.doc);
  const view = useSound((s) => s.view);
  const variant = useSound((s) => s.variant);
  const saveState = useSound((s) => s.saveState);
  const canUndo = useSound((s) => s.past.length > 0);
  const canRedo = useSound((s) => s.future.length > 0);
  const activePattern = useSound((s) => s.activePattern);
  const s = useSound.getState();
  return (
    <div className="viewport-toolbar snd-header">
      <SoundPicker />
      {doc && (
        <>
          <input
            key={doc.name}
            className="snd-name"
            defaultValue={doc.name}
            aria-label="Sound name"
            title="Name of the sound"
            onBlur={(e) => s.rename(e.target.value.trim())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') {
                (e.target as HTMLInputElement).value = doc.name;
                (e.target as HTMLInputElement).blur();
              }
            }}
          />
          <div className="tb-group segmented" role="radiogroup" aria-label="Kind">
            {SOUND_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={doc.kind === k}
                className={`tb-btn${doc.kind === k ? ' is-active' : ''}`}
                title={`${KIND_LABEL[k]} — switching adds what the kind needs and keeps the rest`}
                onClick={() => s.setKind(k)}
              >
                {KIND_LABEL[k]}
              </button>
            ))}
          </div>
        </>
      )}
      <button type="button" className="tb-btn" title="Start a new sound from a preset (opens the preset browser)" onClick={() => s.setSide('presets')}>
        <Icon name="sparkles" size={13} /> From preset
      </button>
      {doc?.variants?.length ? (
        <label className="snd-inline" title="Render a variant (overrides variables/seed)">
          <span className="muted">Variant</span>
          <select value={variant ?? ''} onChange={(e) => s.setVariant(e.target.value || null)} aria-label="Variant">
            <option value="">base</option>
            {doc.variants.map((v) => (
              <option key={v.name} value={v.name}>
                {v.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {doc && (
        <div className="tb-group segmented" role="tablist" aria-label="Editor view">
          <button type="button" role="tab" aria-selected={view === 'timeline'} className={`tb-btn${view === 'timeline' ? ' is-active' : ''}`} onClick={() => s.setView('timeline')}>
            Timeline
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view !== 'timeline'}
            className={`tb-btn${view !== 'timeline' ? ' is-active' : ''}`}
            onClick={() => {
              const name = activePattern ?? Object.keys(doc.patterns ?? {})[0] ?? null;
              if (name) s.openPattern(name);
              else s.setView('pianoRoll');
            }}
            title="Edit the open pattern (piano roll or drum steps)"
          >
            Pattern
          </button>
        </div>
      )}
      <span className="tb-spacer" />
      {doc && <RenderStatus />}
      <div className="tb-group segmented">
        <IconButton icon="undo" title="Undo" shortcut="Ctrl+Z" disabled={!canUndo} onClick={s.undo} />
        <IconButton icon="redo" title="Redo" shortcut="Ctrl+Y" disabled={!canRedo} onClick={s.redo} />
      </div>
      {doc && <SaveIndicator state={saveState} />}
    </div>
  );
}
